import React, { useState, useMemo, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "../../css/barcodeComponent.css";
import "primeflex/primeflex.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primeicons/primeicons.css";
import { useSetRecoilState, useRecoilValue, useRecoilState } from "recoil";
import { orderInfoIsViewState } from "../../stores/barcode/orderInfoIsViewState";
import { barcodeDataState } from "../../stores/barcode/barcodeDataState";
import { trackingValueState } from "../../stores/barcode/trackingValueState";
import { createTitle } from "../../utils/createTitle";
import { loginState } from "../../stores/loginState";
import useGetBarcodeData from "../../api/barcode/useGetBarcodeData";
import { useSendInvoice } from "../../api/barcode/useSendInvoice";
import { useGetTracking } from "../../api/barcode/useGetTracking";
import Loading from "../Loading";
import { formatNumberCommas } from "../../utils/formatNumberCommas";
import { downloadExcel } from "./DownloadExcel";
import Swal from "sweetalert2";
import { isBarcodeLoading } from "../../stores/barcode/isBarcodeLoading";

export default function BarcodeListsComponent(props) {
  // 상태값 선언
  const login = useRecoilValue(loginState);
  const barcodeData = useRecoilValue(barcodeDataState);
  const setOrderInfoIsView = useSetRecoilState(orderInfoIsViewState);
  const [trackingValue, setTrackingValue] = useRecoilState(trackingValueState);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [isLoading, setIsLoading] = useRecoilState(isBarcodeLoading);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [workType, setWorkType] = useState("전체");
  const [barcode, setBarcode] = useState("");
  const [resultCode, setResultCode] = useState("전체");

  // 운송장 조회 API
  const { mutateAsync: getBarcodeData } = useGetBarcodeData({
    startDate,
    endDate,
    workType,
    barcode,
    resultCode,
  });

  // 대상 고객에게 송장 전송 API
  const { mutate: sendInvoiceMutate } = useSendInvoice({ startDate, endDate });

  // 운송장 추적 API
  const { mutate: getTracking } = useGetTracking(trackingValue.barcode);

  // 운송장 데이터 갱신 시 날짜 범위 동기화
  useEffect(() => {
    setOrderInfoIsView(false);

    if (barcodeData && barcodeData.length > 0) {
      const dates = barcodeData.map((data) => new Date(data.scandate));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      setStartDate(minDate.toISOString().split("T")[0]);
      setEndDate(maxDate.toISOString().split("T")[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barcodeData]);

  // 테이블 선택 처리
  const onSelectionChange = (e) => {
    setSelectedProduct(e.value);
    setOrderInfoIsView(!!e.value);
  };

  // 기본 제목 구성
  const title = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "차량번호", key: "car_num" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업소", key: "tg_bran_cd" },
        { label: "출발영업사원", key: "pob" },
        { label: "이미지 존재여부", key: "img_exists" },
        { label: "스캐너", key: "dev_serial" },
      ]),
    [],
  );

  // 로젠 제목 구성
  const logenTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "차량번호", key: "car_num" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업소", key: "tg_bran_cd" },
        { label: "출발영업사원", key: "pob" },
        { label: "이미지 존재여부", key: "img_exists" },
        { label: "스캐너", key: "dev_serial" },
        { label: "결과 코드", key: "result_no" },
        { label: "결과 메시지", key: "result_msg" },
      ]),
    [],
  );

  // 한진 제목 구성
  const hanjinTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "간선명", key: "trk" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업사원", key: "pob" },
      ]),
    [],
  );

  // 한덱스 제목 구성
  const handexTitle = useMemo(
    () =>
      createTitle([
        { label: "운송장번호", key: "barcode" },
        { label: "스캔일자", key: "scandate" },
        { label: "스캔시간", key: "scantime" },
        { label: "업무구분", key: "tm_dv" },
        { label: "카메라 ID", key: "scannername" },
        { label: "작업영업소", key: "bran_cd" },
        { label: "작업영업사원", key: "emp_cd" },
        { label: "출발영업사원", key: "pob" },
      ]),
    [],
  );

  // 회사별 제목 선택
  const selectedTitle = useMemo(() => {
    const company = login.company?.toUpperCase();
    if (company === "HANJIN") {
      return hanjinTitle;
    }
    if (company === "HANDEX") {
      return handexTitle;
    }
    if (company === "LOGEN") {
      return logenTitle;
    }
    return title;
  }, [login.company, title, logenTitle, hanjinTitle, handexTitle]);

  // 고객사별 자동 전송 텍스트
  const companyAutoComment =
    login.company?.toUpperCase() === "LOGEN"
      ? "iLogen"
      : login.company?.toUpperCase() === "LOTTE"
        ? "롯데"
        : "";

  // 송장 전송
  const onClickSendInvoice = () => {
    sendInvoiceMutate();
  };

  // 조회 실행
  const onClickSearch = async () => {
    if (startDate <= endDate) {
      setIsLoading(true);
      try {
        await getBarcodeData();
      } catch (error) {
        console.error("운송장 데이터 조회 중 오류가 발생했습니다.", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      Swal.fire({
        icon: "warning",
        title: "조회일자가 올바르지 않습니다.",
        confirmButtonText: "확인",
      });
    }
    setOrderInfoIsView(false);
  };

  // 업무구분 표시 변환
  const workTypeTmdv = (rowData) => {
    if (rowData.tm_dv === "60") {
      return "배송입고(60)";
    }
    if (rowData.tm_dv === "30") {
      return "집하출고(30)";
    }
    if (rowData.tm_dv === "21") {
      return "도착(21)";
    }
    if (rowData.tm_dv === "20") {
      return "발송(20)";
    }
    if (rowData.tm_dv === "31") {
      return "간선도착(31)";
    }
    if (rowData.tm_dv === "32") {
      return "간선출발(32)";
    }
    if (rowData.tm_dv === "14") {
      return "배송완료(14)";
    }
    if (rowData.tm_dv === "DD") {
      return "배송인수확인(DD)";
    }
    return rowData.tm_dv;
  };

  // row_num 포맷
  const workTypeRownum = (rowData) => {
    if (rowData.row_num) {
      return formatNumberCommas(rowData.row_num);
    }
    return rowData.row_num;
  };

  // 이미지 존재 여부
  const workTypeImg = (rowData) => {
    return rowData.img_exists ? "O" : "X";
  };

  // 스캐너 구분 표시
  const workTypeSerial = (rowData) => {
    if (rowData.dev_serial === "1541084") {
      return "스토어";
    }
    if (rowData.dev_serial === "1793913") {
      return "PDA";
    }
    return "기타";
  };

  // 행 선택 시 추적 조회
  const handleRowClick = async (rowData) => {
    setTrackingValue({
      barcode: rowData.barcode,
      scanDate: rowData.scandate,
    });
    setTimeout(() => {
      getTracking();
    }, 0);
  };

  // Excel 다운로드
  const onClickExcelDownload = () => {
    downloadExcel(
      barcodeData,
      `${startDate === endDate ? startDate : `${startDate}~${endDate}`} 운송장데이터조회.xlsx`,
    );
  };

  // Enter 검색
  const handleKeyDownEnter = (e) => {
    if (e.key === "Enter") {
      onClickSearch();
    }
  };

  return (
    <div
      onKeyDown={handleKeyDownEnter}
      className="card"
      style={{
        width: "100%",
        maxWidth: "1200px",
        minHeight: "800px",
        height: "calc(100vh - 40px)",
        border: `1px solid ${props.theme.white4}`,
        background: props.theme.white1,
        color: props.theme.black1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isLoading && <Loading />}
      <span
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          padding: 0,
        }}
      >
        <h3 style={{ position: "relative", left: "0", margin: "0" }}>
          운송장관리
        </h3>
        <span style={{ display: "flex", padding: 0 }}>
          <div
            style={{
              textAlign: "right",
              marginLeft: "30px",
              color: props.theme.black1,
              fontSize: "16px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {login.company?.toUpperCase() === "LOGEN"
                ? "배송입고 (60) "
                : login.company?.toUpperCase() === "LOTTE"
                  ? "도착 (21) "
                  : "간선출발 (32) "}
              :{" "}
              <p style={{ width: "100px" }}>
                {barcodeData
                  ? formatNumberCommas(
                      barcodeData.filter(
                        (item) =>
                          item.tm_dv === "60" ||
                          item.tm_dv === "21" ||
                          item.tm_dv === "32" ||
                          item.tm_dv === "15",
                      ).length,
                    )
                  : 0}{" "}
                건
              </p>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {login.company?.toUpperCase() === "LOGEN"
                ? "집하출고 (30) "
                : login.company?.toUpperCase() === "LOTTE"
                  ? "발송 (20) "
                  : "간선도착 (31) "}
              :{" "}
              <p style={{ width: "100px" }}>
                {barcodeData
                  ? formatNumberCommas(
                      barcodeData.filter(
                        (item) =>
                          item.tm_dv === "30" ||
                          item.tm_dv === "20" ||
                          item.tm_dv === "31" ||
                          item.tm_dv === "50",
                      ).length,
                    )
                  : 0}{" "}
                건
              </p>
            </div>
          </div>

          {(login.company?.toUpperCase() === "HANJIN" ||
            login.company?.toUpperCase() === "HANDEX") && (
            <div
              style={{
                textAlign: "right",
                marginLeft: "30px",
                color: props.theme.black1,
                fontSize: "16px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {"배송완료 (14) "}:{" "}
                <p style={{ width: "100px" }}>
                  {barcodeData
                    ? formatNumberCommas(
                        barcodeData.filter((item) => item.tm_dv === "14")
                          .length,
                      )
                    : 0}{" "}
                  건
                </p>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {"배송인수확인 (DD) "}:{" "}
                <p style={{ width: "100px" }}>
                  {barcodeData
                    ? formatNumberCommas(
                        barcodeData.filter((item) => item.tm_dv === "DD")
                          .length,
                      )
                    : 0}{" "}
                  건
                </p>
              </div>
            </div>
          )}
          {(login.company?.toUpperCase() === "LOGEN" ||
            login.company?.toUpperCase() === "LOTTE") && (
            <button
              style={{
                padding: "5px 15px",
                border: `1px solid ${props.theme.white1}`,
                background: props.theme.colorwhite1,
                color: props.theme.black1,
                borderRadius: "10px",
                marginLeft: "50px",
              }}
              onClick={onClickSendInvoice}
            >
              {companyAutoComment} 자동전송
            </button>
          )}
          <button
            style={{
              padding: "5px 15px",
              border: `1px solid ${props.theme.white1}`,
              background: props.theme.colorwhite1,
              color: props.theme.black1,
              borderRadius: "10px",
              marginLeft: "20px",
            }}
            onClick={onClickExcelDownload}
          >
            Excel 다운로드
          </button>
        </span>
      </span>
      <span
        className="filter"
        style={{
          border: `1px solid ${props.theme.white1}`,
          background: props.theme.white2,
          color: props.theme.black1,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          padding: "10px",
          height: "auto",
          gap: "10px 20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
        >
          <p style={{ margin: 0 }}>조회일자</p>
          <input
            type="date"
            className="date"
            style={{ background: props.theme.white0 }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          -
          <input
            type="date"
            className="date"
            style={{ background: props.theme.white0 }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div
          style={{
            color: props.theme.white4,
            display: window.innerWidth > 1200 ? "block" : "none",
          }}
        >
          |
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
        >
          <p style={{ margin: 0 }}>업무구분</p>
          <select
            className="date"
            value={workType}
            onChange={(e) => setWorkType(e.target.value)}
            style={{ background: props.theme.white0, width: "120px" }}
          >
            <option value="전체">전체</option>
            <option
              value={
                login.company?.toUpperCase() === "LOGEN"
                  ? "60"
                  : login.company?.toUpperCase() === "LOTTE"
                    ? "21"
                    : login.company?.toUpperCase() === "HANJIN"
                      ? "31"
                      : "15"
              }
            >
              {login.company?.toUpperCase() === "LOGEN"
                ? "배송입고 (60)"
                : login.company?.toUpperCase() === "LOTTE"
                  ? "도착 (21)"
                  : "간선출발 (32)"}
            </option>
            <option
              value={
                login.company?.toUpperCase() === "LOGEN"
                  ? "30"
                  : login.company?.toUpperCase() === "LOTTE"
                    ? "20"
                    : login.company?.toUpperCase() === "HANJIN"
                      ? "32"
                      : "50"
              }
            >
              {login.company?.toUpperCase() === "LOGEN"
                ? "집하출고 (30)"
                : login.company?.toUpperCase() === "LOTTE"
                  ? "발송 (20)"
                  : "간선도착 (31)"}
            </option>
          </select>
        </div>

        {login.company?.toUpperCase() === "LOGEN" && (
          <>
            <div
              style={{
                color: props.theme.white4,
                display: window.innerWidth > 1200 ? "block" : "none",
              }}
            >
              |
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                whiteSpace: "nowrap",
              }}
            >
              <p style={{ margin: 0 }}>결과 코드</p>
              <select
                className="date"
                value={resultCode}
                onChange={(e) => setResultCode(e.target.value)}
                style={{ background: props.theme.white0, width: "100px" }}
              >
                <option value="전체">전체</option>
                <option value="99">성공 (99)</option>
                <option value="0">실패 (0)</option>
              </select>
            </div>
          </>
        )}

        <div
          style={{
            color: props.theme.white4,
            display: window.innerWidth > 1200 ? "block" : "none",
          }}
        >
          |
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            whiteSpace: "nowrap",
          }}
        >
          <p style={{ margin: 0 }}>운송장 번호</p>
          <input
            placeholder="번호를 입력해주세요."
            className="date"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            style={{ background: props.theme.white0, width: "160px" }}
          />
          <button
            className="date"
            id="button"
            style={{ background: props.theme.white3, width: "70px" }}
            onClick={onClickSearch}
          >
            검색
          </button>
        </div>
      </span>
      <DataTable
        value={barcodeData}
        rows={25}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{
          minWidth:
            login.company?.toUpperCase() === "LOGEN" ? "1500px" : "100%",
        }}
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        paginatorLeft={
          <div
            style={{
              position: "absolute",
              left: 0,
              color: props.theme.black1,
              fontSize: "18px",
              bottom: "15px",
            }}
          >
            총 검색 결과:{" "}
            {barcodeData ? formatNumberCommas(barcodeData.length) : 0} 건
          </div>
        }
        paginatorRight={<></>}
        selectionMode="single"
        selection={selectedProduct}
        onSelectionChange={onSelectionChange}
        dataKey="id"
        emptyMessage=" "
        paginator
        paginatorClassName="Indigo 200"
        scrollable
        scrollHeight="calc(100vh - 350px)"
      >
        {selectedTitle.map((col, index) => (
          <Column
            key={col.key || index}
            field={col.key}
            header={col.label}
            sortable={false}
            body={(rowData) => (
              <div
                onClick={() => handleRowClick(rowData)}
                title={rowData[col.key] ?? ""}
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {col.key === "row_num"
                  ? workTypeRownum(rowData)
                  : col.key === "tm_dv"
                    ? workTypeTmdv(rowData)
                    : col.key === "img_exists"
                      ? workTypeImg(rowData)
                      : col.key === "dev_serial"
                        ? workTypeSerial(rowData)
                        : (rowData[col.key] ?? "")}
              </div>
            )}
            headerStyle={{
              backgroundColor: props.theme.white2,
              color: props.theme.black1,
              border: `1px solid ${props.theme.white4}`,
              zIndex: 1,
            }}
            style={{
              fontSize: "13px",
              width: col.key === "result_msg" ? "250px" : "max-content",
              minWidth: "120px",
              height: "10px",
              padding: "5px 10px",
              border: `1px solid ${props.theme.white4}`,
              backgroundColor: "unset",
              color: props.theme.black1,
              textAlign: "center",
              cursor: "pointer",
            }}
          />
        ))}
      </DataTable>
    </div>
  );
}
